import { Toggle as TogglePrimitive } from "@base-ui/react/toggle"
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group"
import { type VariantProps } from "class-variance-authority"
import { cn } from "@/modules/lib/utils"
import { chipsVariants } from "@/components/ui/baseUi/chip"
import { createContext, useContext } from "react"

const ChipGroupContext = createContext<
  VariantProps<typeof chipsVariants> & {
    spacing?: number
    orientation?: "horizontal"
  }
>({
  size: "md",
  variant: "emphasisLow",
  spacing: 0,
  orientation: "horizontal",
})

function ChipGroup({
  className,
  variant,
  size,
  spacing = 0,
  orientation = "horizontal",
  children,
  ...props
}: ToggleGroupPrimitive.Props &
  VariantProps<typeof chipsVariants> & {
    spacing?: number
    orientation?: "horizontal"
  }) {
  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      data-orientation={orientation}
      style={{ "--gap": spacing } as React.CSSProperties}
      className={cn("group/toggle-group flex w-fit flex-row items-center gap-[--spacing(var(--gap))]", className)}
      {...props}
    >
      <ChipGroupContext.Provider value={{ variant, size, spacing, orientation }}>{children}</ChipGroupContext.Provider>
    </ToggleGroupPrimitive>
  )
}

function ChipGroupItem({
  className,
  children,
  variant = "emphasisLow",
  size = "md",
  ...props
}: TogglePrimitive.Props & VariantProps<typeof chipsVariants>) {
  const context = useContext(ChipGroupContext)

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      data-spacing={context.spacing}
      className={cn(
        "shrink-0",
        chipsVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  )
}

export { ChipGroup, ChipGroupItem }
